import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import styled from "styled-components"

import { useLazyGetAlertsQuery } from "store/api/alertsApi"
import { useAppSelector } from "store/hooks"
import { ApiTypes, UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"
import { ReactComponent as IconEye } from "assets/svg/icon-eye.svg"

import { convertAlertsToTableRows } from "utils/convertData"
import { isMobileDevice } from "utils/deviceDetection"
import getErrorMessage from "utils/getErrorMessage"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import { useAlertViewDetails } from "hooks/useAlertViewDetails"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { SubscriptionUpgradeModal } from "modals/SubscriptionUpgradeModal/SubscriptionUpgradeModal"
import { AlertsActions } from "components/common/AlertsActions"
import { Pagination } from "components/common/Pagination"

import { dummyAlerts, dummyMeta } from "../../AlertsPage/dummyAlertsData"
import { useCustomerDashboard } from "../CustomerDashboardContext"
import { StyledTableNavWrapper, StyledTableWrapper } from "../styled"
import { customerDashboardAlertsTableHeaderCells } from "./customerDashboardAlertsTableHeaderCells"

const AlertsContainer = styled.div<{ sidebarOpen?: boolean }>`
  margin-top: 2px;
  padding: 23px ${({ sidebarOpen }) => (sidebarOpen ? "24px" : "0")} 20px 0;
  max-height: calc(100dvh - 255px);
  overflow-y: auto;
`

export const CustomerDashboardAlerts = () => {
  const userProfile = useAppSelector(state => state.user.userProfile)
  const { devices, currentDevice, isSidebarOpen } = useCustomerDashboard()
  const [triggerGetAlerts, { isFetching, data }] = useLazyGetAlertsQuery()
  const [selectedAlerts, setSelectedAlerts] = useState<ApiTypes.Model.Alert[]>([])
  const [isHeaderChecked, setIsHeaderChecked] = useState(false)
  const { urlQueryParams } = useUrlQueryParams()
  const {
    selectedDevice: selectedDeviceForModal,
    monitoringReportAnnotations,
    handleViewDetails,
    clearMonitoringReportAnnotations,
    closeStatsModal,
  } = useAlertViewDetails()

  const hasAlertsAccess = userProfile ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.Alerts) : false

  const selectedDevice = devices[currentDevice]
  const deviceSN = selectedDevice?.sn

  const sendRequest = useCallback(async () => {
    if (!deviceSN || !hasAlertsAccess) return
    try {
      const requestParams = { ...urlQueryParams, sn: deviceSN }
      const res = await triggerGetAlerts(requestParams)
      const error = getErrorMessage(res?.error)
      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }, [triggerGetAlerts, urlQueryParams, deviceSN, hasAlertsAccess])

  useEffect(() => {
    sendRequest()
  }, [urlQueryParams, sendRequest])

  const allAlertsSelected = useMemo(() => {
    const alertsList = data?.data || []
    if (alertsList.length === 0) return false
    return alertsList.every(item => selectedAlerts.findIndex(alert => alert.id === item.id) > -1)
  }, [data?.data, selectedAlerts])

  useEffect(() => {
    setIsHeaderChecked(allAlertsSelected)
  }, [allAlertsSelected])

  const tableHeaderCells = customerDashboardAlertsTableHeaderCells

  const onSelectAlert = (id: number) => {
    const alertsList = data?.data || []
    if (selectedAlerts.some(item => item.id === id)) {
      setSelectedAlerts(selectedAlerts.filter(item => item.id !== id))
    } else {
      setSelectedAlerts([...selectedAlerts, alertsList.find(item => item.id === id)!])
    }
  }

  const handleSuccess = () => {
    sendRequest()
    setSelectedAlerts([])
  }

  const toggleAllAlerts = () => {
    const alertsList = data?.data || []
    if (isHeaderChecked) {
      setSelectedAlerts([])
      setIsHeaderChecked(false)
    } else {
      setSelectedAlerts(alertsList)
      setIsHeaderChecked(true)
    }
  }

  const getRowActions = (rowData: UITypes.TableBodyRow) => {
    if (rowData.type?.value !== "Efficiency Issue") {
      return {
        className: "",
        actionButtons: [],
      }
    }

    return {
      actionButtons: [
        {
          icon: <IconEye />,
          tooltip: "View on chart",
          tooltipPosition: "top-start" as const,
          hidden: rowData.rowSettings.isEnable === false,
          handler: () => handleViewDetails(rowData),
        },
      ],
    }
  }

  const alertsList = hasAlertsAccess ? data?.data || [] : dummyAlerts
  const meta = hasAlertsAccess ? data?.meta : dummyMeta

  return (
    <>
      <AlertsContainer sidebarOpen={isSidebarOpen}>
        <StyledTableNavWrapper>
          <AlertsActions
            selectedAlerts={selectedAlerts}
            onSuccess={handleSuccess}
            selectedDevice={selectedDeviceForModal}
            monitoringReportAnnotations={monitoringReportAnnotations}
            onClearMonitoringReportAnnotations={clearMonitoringReportAnnotations}
            onCloseStatsModal={closeStatsModal}
          />
        </StyledTableNavWrapper>
        <StyledTableWrapper
          headerCells={tableHeaderCells}
          columnsLoaded={true}
          data={convertAlertsToTableRows(alertsList)}
          isLoading={isFetching && hasAlertsAccess}
          onCheckboxChange={onSelectAlert}
          currentItemIDs={selectedAlerts.map(item => item.id)}
          onHeaderCheckboxChange={toggleAllAlerts}
          isHeaderChecked={isHeaderChecked}
          isMobileDevice={isMobileDevice()}
          fitContainer={true}
          getRowActions={getRowActions}
        />
        {meta && meta.last_page > 1 && <Pagination meta={meta} />}
      </AlertsContainer>
      {!hasAlertsAccess && <SubscriptionUpgradeModal page={SubscriptionPrivilege.Alerts} />}
    </>
  )
}
